<template>
  <Modal
    ref="createTrainerModal"
    :title="modalTitle"
    @closed="resetData"
  >
    <!-- <template #modal-header /> -->
    <template #modal-main>
      <div class="settings scrollbar">
        <div class="container">
          <div class="static-card">
            <h2>Description</h2>

            <HelpTip>
              A training session enables you to train a model based on the selected model type.
              The duration of the session can range from a few minutes to several days,
              depending on the size of the dataset and the number of training epochs.
            </HelpTip>

            <div class="row mt-3">
              <div class="col-12 settings__control-group">
                <label class="required">Trainer Type</label>
                <select v-model="schemaType" class="select" required>
                  <option :value="''" disabled>Select a Trainer Type</option>
                  <template v-for="schema in schemaList" :key="schema.name">
                    <option :value="schema.name">{{ schema.label }}</option>
                  </template>
                </select>
              </div>
            </div>

            <div class="mt-4 mb-2">
              <h2 class="mb-2">Select Manage Type</h2>

              <div>
                <label>
                  <input
                    v-model="manage_type"
                    type="radio"
                    value="cloud"
                    name="manage_group"
                    class="split-radio"
                  >
                  Cloud Managed
                </label>
              </div>

              <div>
                <label>
                  <input
                    v-model="manage_type"
                    type="radio"
                    value="local"
                    name="manage_group"
                    class="split-radio"
                  >
                  User Managed
                </label>
              </div>

              <div>
                <label>
                  <input
                    v-model="manage_type"
                    type="radio"
                    value="kubernetes"
                    name="manage_group"
                    class="split-radio"
                  >
                  Kubernetes Managed
                </label>
              </div>
            </div>

            <HelpTip>
              By default, a training session runs on a cloud server instance equipped with GPU resources.
              However, you have the option to enable a "User Managed Trainer," allowing you to run a custom training script.
              Edgefirst Studio APIs provide the endpoints needed to interact with Edgefirst Studio backend server.
              For more details, please refer to the <a href="https://doc.edgefirst.ai/latest/perception/studio/" target="_blank" rel="noopener noreferrer">Edgefirst Help documentation</a>.
            </HelpTip>

            <div class="row mt-3">
              <div class="col-12 settings__control-group">
                <label class="required">Name</label>
                <input
                  v-model="name"
                  class="select"
                  type="text"
                >
              </div>
            </div>
            <div class="row mt-3">
              <div class="col-12 settings__control-group">
                <label>Description (Optional)</label>
                <textarea
                  v-model="description"
                  rows="2"
                />
              </div>
            </div>
            <h2 class="mt-4">Training Data</h2>
            <div class="row mt-3">
              <div class="col-auto">
                <input
                  id="percent-split-radio"
                  v-model="groupSplit"
                  type="radio"
                  name="percent_group_split"
                  value="percent"
                  class="split-radio"
                >
              </div>
              <div class="col settings__control-group">
                <label class="clickable" @click="() => groupSplit='percent'">From Dataset</label>
                <template v-if="groupSplit ==='percent'">
                  <div class="row mt-2 full-width">
                    <div class="settings__control-group dataset-selector">
                      <label class="required">Dataset</label>
                      <select v-model="dataset" class="select" required>
                        <option disabled :value="''">Select a Dataset</option>
                        <option v-for="(d, i) in datasetList" :key="i" :value="d">
                          {{ d.name }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div v-if="dataset" class="row mt-2 full-width">
                    <div class="settings__control-group dataset-selector">
                      <label class="required">Annotation Set</label>
                      <select v-model="annset" class="select" required>
                        <option disabled :value="''">Select Annotation Set</option>
                        <option v-for="(a, i) in dataset.annotation_sets" :key="i" :value="a">
                          {{ a.name }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div v-if="dataset" class="row mt-2 full-width">
                    <div class="settings__control-group dataset-selector">
                      <label class="required">Training Group</label>
                      <select v-model="trainGroup" class="select" required>
                        <option disabled :value="''">Select Training Group</option>
                        <option v-for="(group, i) in dataset.groups" :key="i" :value="group">
                          {{ group.name }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div v-if="dataset" class="row mt-2 full-width">
                    <div class="settings__control-group dataset-selector">
                      <label class="required">Validation Group</label>
                      <select v-model="valGroup" class="select" required>
                        <option disabled :value="''">Select Validation Group</option>
                        <option v-for="(group, i) in dataset.groups" :key="i" :value="group">
                          {{ group.name }}
                        </option>
                      </select>
                    </div>
                  </div>
                </template>
              </div>
            </div>
            <div class="row mt-3">
              <div class="col-12">
                <v-checkbox
                  v-model="use_default_weights"
                  color="#250e81"
                  :hide-details="true"
                  :density="'compact'"
                >
                  <template #label>
                    <span class="checkbox-label">Use Default Weights</span>
                  </template>
                </v-checkbox>
              </div>
            </div>
            <div v-if="!use_default_weights" class="row ms-2">
              <div class="col-12">
                <div class="row">
                  <div class="col-12 settings__control-group dataset-selector">
                    <label class="required">Experiment</label>
                    <select v-model="weightsExperiment" class="select" required>
                      <option disabled :value="''">Select an Experiment</option>
                      <option v-for="(exp, i) in experiments" :key="i" :value="exp">
                        {{ exp.name }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="row mt-2">
                  <div class="col-12 settings__control-group dataset-selector">
                    <label class="required">Training Session</label>
                    <select v-model="weightsTrainingSession" class="select" required>
                      <option disabled :value="''">Select a Training Session</option>
                      <option v-for="(ts, i) in weightsTrainingSessions" :key="i" :value="ts">
                        {{ ts.name }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="dynamic-card">
            <h2>Model Settings</h2>
            <div class="container pt-2">
              <ConfigureFieldsGroup
                :key="componentKey"
                v-model:params="configGroupValues"
                :fields="schema"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #modal-footer>
      <div class="footer">
        <div v-if="message" class="result error">
          <span>{{ message }}</span>
        </div>
        <button
          class="button modal-action-button"
          :class="{'button-spinner': startingTrainer, 'mt-2': message}"
          :disabled="startingTrainer"
          @click="startSessionV2"
        >
          Start Session
        </button>
      </div>
    </template>
  </Modal>
</template>

<script>
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import Modal from '@/components/Modal.vue';
import { inputSanitizer, changeSanitizer } from '@/assets/js/utils';
import ConfigureFieldsGroup from '@/components/TrainerComponent/TrainerExperimentAndSessions/ConfigureFieldsGroup.vue';
import {
  computed,
} from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import useTaskModal from '@/composables/useTaskModal.js';
import { event } from 'vue-gtag';
import HelpTip from '../../HelpTip.vue';

const $toast = useToast();

export default {
  name: 'CreateTrainerSessionModal',
  components: {
    Modal,
    ConfigureFieldsGroup,
    HelpTip,
  },
  props: {
    trainer: {
      type: Number,
      default: 0,
    },
    trainerSession: {
      type: Object,
      default: null,
    },
    experiments: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['create-trainer-session'],
  setup() {
    const store = useStore();
    const currentProject = computed(() => store.state.projects.currentProject);
    const {
      pauseGetTasks,
      resumeGetTasks,
      isGettingTasks,
      getTasks,
    } = useTaskModal();
    return {
      pauseGetTasks,
      resumeGetTasks,
      isGettingTasks,
      getTasks,
      currentProject,
    };
  },
  data() {
    return {
      modalTitle: "Create Training Session",
      schemaType: 'modelpack',
      message: '',
      name: '',
      description: "",
      dataset: '',
      annset: '',
      trainGroup: '',
      valGroup: '',
      groupSplit: 'percent',
      version_params: {
        version: '',
      },
      custom_split_slider_values: [80, 20, 0],
      custom_split_train_input: 0,
      custom_split_val_input: 0,
      dataset_split_params: {
        train: {
          d: '',
          set: '',
          group: '',
        },
        val: {
          d: '',
          set: '',
          group: '',
        },
        test: {
          d: '',
          set: '',
          group: '',
        },
      },
      default_split_params: {
        train: {
          d: '',
          set: '',
        },
        val: {
          d: '',
          set: '',
        },
        test: {
          d: '',
          set: '',
        },
      },
      modelList: [
        { name: 'ModelPack', value: 'modelpack' },
        { name: 'Legacy Detection', value: 'legacy' },
        { name: 'Legacy Segmentation', value: 'legacy' },
      ],
      selectedModel: null,
      startingTrainer: false,
      advancedSettings: {
        upsample: 'resize',
      },
      schema: null,
      schemaList: [],
      configGroupValues: {},
      componentKey: 0,
      manage_type: "cloud",
      use_default_weights: true,
      weightsExperiment: '',
      weightsTrainingSessions: [],
      weightsTrainingSession: '',
    };
  },
  computed: {
    is_aws() {
      return this.manage_type === "cloud";
    },
    is_local() {
      return this.manage_type === "local";
    },
    is_kubernetes() {
      return this.manage_type === "kubernetes";
    },
    isDebugMode() {
      return this.$store.state.debugMode;
    },
    datasetList() {
      return this.$store.state.datasets.datasetList;
    },
    custom_split_params() {
      return {
        train: {
          percent: this.custom_split_slider_values[0],
          count: (this.custom_split_slider_values[0] / 100) * this.total,
        },
        val: {
          percent: this.custom_split_slider_values[1],
          count: (this.custom_split_slider_values[1] / 100) * this.total,
        },
        test: {
          percent: this.custom_split_slider_values[2],
          count: (this.custom_split_slider_values[2] / 100) * this.total,
        },
      };
    },
    total() {
      if (this.dataset) {
        return this.dataset.numImages;
      }
      return 0;
    },
  },
  watch: {
    trainerSession() {
      if (this.trainerSession == null) {
        this.modalTitle = "Create Training Session";
        return;
      }

      const sid = `v-${this.trainerSession.id.toString(16)}`;
      this.modalTitle = `Clone Training Session: Source ID: ${sid}`;

      //Copy Schema type
      this.schemaType = this.trainerSession.model;

      this.SetDatasetFromSourceTrainer(this.trainerSession);
      this.SetSchemaDefaultsFromSourceTrainer(this.trainerSession);
    },
    configGroupValues: {
      deep: true,
      handler(value) {
        // console.log(value);
      },
    },
    datasetList(l) {
      if (l && l.length > 0) {
        this.dataset = l[0];
      }
    },
    dataset(d) {
      this.annset = '';
      this.trainGroup = '';
      this.valGroup = '';

      if (d.annotation_sets && d.annotation_sets.length > 0) {
        this.annset = d.annotation_sets[0];
      }
      if (d.groups && d.groups.length > 0) {
        const trainGroup = d.groups.find((group) => group.name.includes("train"));
        if (trainGroup) {
          this.trainGroup = trainGroup;
        }
        const valGroup = d.groups.find((group) => group.name.includes("val"));
        if (valGroup) {
          this.valGroup = valGroup;
        }
      }
    },
    schemaType(v) {
      this.schema = null;
      this.configGroupValues = {};
      this.getTrainerSchema(v);
    },
    async weightsExperiment(v) {
      if (v) {
        this.weightsTrainingSessions = await this.getTrainingSessionList(v.id);
      }
    },
  },
  created() {
    this.dataConnect = new DatastoreConnect(this.$store.state.enterpriseServerUrl);
    if (this.datasetList && this.datasetList.length > 0) {
      this.dataset = this.datasetList[0];
    }
  },
  mounted() {
    this.getAllTrainerSchema();
    this.getTrainerSchema(this.schemaType);
  },
  methods: {
    inputSanitizer,
    changeSanitizer,
    async getTrainerSchema(schemaType) {
      const params = {
        type: schemaType,
      };
      await this.dataConnect.getTrainerParametersSchema(params)
        .then((data) => {
          if (data.result) {
            this.schema = data.result;
          } else if (data.error) {
            this.message = data.error.message;
          }
          this.componentKey += 1;
        });
    },
    async getAllTrainerSchema() {
      await this.dataConnect.getTrainerParametersSchema()
        .then((data) => {
          if (data.result) {
            this.schemaList = data.result?.schema_list;
            
            // this.schemaList.push({label: 'Yolov4', name: 'fusion', schema_type: 'fusion'});
            // this.schemaList.push({label: 'Yolov5', name: 'fusion', schema_type: 'fusion'});
            // this.schemaList.push({label: 'Yolov8', name: 'fusion', schema_type: 'fusion'});
            // this.schemaList.push({label: 'Yolo11', name: 'fusion', schema_type: 'fusion'});
            // this.schemaList.push({label: 'YoloX', name: 'fusion', schema_type: 'fusion'});
            // this.schemaList.push({label: 'DETR', name: 'fusion', schema_type: 'fusion'});
            // this.schemaList.push({label: 'Monocular Depth', name: 'fusion', schema_type: 'fusion'});
            // this.schemaList.push({label: 'Stereo Depth', name: 'fusion', schema_type: 'fusion'});
 
          } else if (data.error) {
            this.message = data.error.message;
          }
          this.componentKey += 1;
        })
        .catch((error) => {
          console.log(error);
        });
    },
    showModal() {
      this.$refs.createTrainerModal.showModal();
      if (this.trainerSession == null) {
        this.modalTitle = "Create Training Session";
        this.resetData();
      }
    },
    resetData() {
      this.name = '';
      this.description = '';
      this.dataset = '';
      this.annset = '';
      this.selectedModel = '';
      this.trainMode = 'detect';
      this.device = 'gpu';
      this.message = '';
      this.startingTrainer = false;
      this.groupSplit = 'percent';
      this.dataset_split_params = {
        train: {
          d: '',
          set: '',
          group: 0,
        },
        val: {
          d: '',
          set: '',
          group: 0,
        },
        test: {
          d: '',
          set: '',
          group: 0,
        },
      };
      this.default_split_params = {
        train: {
          d: '',
          set: '',
        },
        val: {
          d: '',
          set: '',
        },
        test: {
          d: '',
          set: '',
        },
      };
      this.version_params = {
        d: '',
        version: '',
      };
      this.use_default_weights = true;
      this.weightsExperiment = '';
      this.weightsTrainingSessions = [];
      this.weightsTrainingSession = '';
    },
    SetDatasetFromSourceTrainer(s) {
      this.name = `${s.name}-COPY`;
      this.description = s.description;
      if (s.params.dataset_params.dataset_id) {
        var ds = this.datasetList.find((d) => d.id === s.params.dataset_params.dataset_id);
        this.dataset = ds;
      }
      if ( this.dataset && s.params.dataset_params.annotation_set_id) {
        const as = ds.annotation_sets.find((a) => a.id === s.params.dataset_params.annotation_set_id);
        this.annset = as;
      }
      this.selectedModel = '';
      this.trainMode = 'detect';
      this.device = 'gpu';
      this.message = '';
      this.startingTrainer = false;
      this.groupSplit = 'percent';
      this.dataset_split_params = {
        train: {
          d: '',
          set: '',
          group: 0,
        },
        val: {
          d: '',
          set: '',
          group: 0,
        },
        test: {
          d: '',
          set: '',
          group: 0,
        },
      };
      this.default_split_params = {
        train: {
          d: '',
          set: '',
        },
        val: {
          d: '',
          set: '',
        },
        test: {
          d: '',
          set: '',
        },
      };
      this.version_params = {
        d: '',
        version: '',
      };
      this.use_default_weights = true;
      this.weightsExperiment = '';
      this.weightsTrainingSessions = [];
      this.weightsTrainingSession = '';
    },
    SetSchemaDefaultsFromSourceTrainer(s) {
      const params = s.params.model_params;
      this.traverse(params, "");
    },

    traverse(obj, path = "") {
      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          this.traverse(item, `${path}[${index}]`);
        });
      } else if (typeof obj === "object" && obj !== null) {
        Object.entries(obj).forEach(([key, value]) => {
          this.traverse(value, path ? `${path}.${key}` : key);
        });
      } else {
        // Set Default in schema
        this.SetSchemaDefaults(path, obj);
      }
    },
    SetSchemaDefaults(path, value) {
      let obj = this.schema;

      const pf = path.split(".");
      for (let k = 0; k < pf.length; k++) {
        const p = pf[k];
        let index = -1;
        if (typeof obj.children !== "undefined") {
          index = obj.children.findIndex((item) => item.name === p);
          if (index < 0) return;
          obj = obj.children[index];
        } else {
          index = obj.findIndex((item) => item.name === p);
          if (index < 0) return;
          obj = obj[index];
        }
      }
      obj.default = value;
    },
    async startSessionV2() {
      if (!this.name) {
        this.message = `Missing Name`;
        return;
      }

      const params = {
        trainer_id: this.trainer,
        split_mode: this.groupSplit,
        weights_session: this.weightsTrainingSession?.id,
        params: this.configGroupValues,
      };

      if (this.groupSplit === 'percent') {
        if (!this.dataset) {
          this.message = `Missing Dataset`;
          return;
        }

        if (!this.annset) {
          this.message = `Missing Annotation Set`;
          return;
        }

        if (!this.trainGroup) {
          this.message = `Missing Training Group`;
          return;
        }

        if (!this.valGroup) {
          this.message = `Missing Validation Group`;
          return;
        }

        if (!this.use_default_weights) {
          if (!this.weightsExperiment) {
            this.message = `Missing Weights Experiment`;
            return;
          }

          if (!this.weightsTrainingSession) {
            this.message = `Missing Weights Training Session`;
            return;
          }
        }

        params['dataset_id'] = this.dataset.id;
        params['annotation_set_id'] = this.annset.id;
        params['train_group_name'] = this.trainGroup.name;
        params['val_group_name'] = this.valGroup.name;
        params['train_split'] = this.custom_split_slider_values[0];
        params['val_split'] = this.custom_split_slider_values[1];
        params['test_split'] = this.custom_split_slider_values[2];
      }
      if (this.groupSplit === 'group') {
        if (!this.dataset_split_params.train.d) {
          this.message = `Missing Train Dataset`;
          return;
        }
        params['train_dataset_id'] = this.dataset_split_params.train.d.id;

        if (!this.dataset_split_params.train.set) {
          this.message = `Missing Train Annotation Set`;
          return;
        }
        params['train_set_id'] = this.dataset_split_params.train.set.id;

        if (this.dataset_split_params.train.group <= 0) {
          this.message = `Missing Train Group`;
          return;
        }
        params['train_group'] = this.dataset_split_params.train.group;

        if (!this.dataset_split_params.val.d) {
          this.message = `Missing Val Dataset`;
          return;
        }
        params['val_dataset_id'] = this.dataset_split_params.val.d.id;

        if (!this.dataset_split_params.val.set) {
          this.message = `Missing Val Annotation Set`;
          return;
        }
        params['val_set_id'] = this.dataset_split_params.val.set.id;

        if (this.dataset_split_params.val.group <= 0) {
          this.message = `Missing Val Group`;
          return;
        }
        params['val_group'] = this.dataset_split_params.val.group;

        if (this.dataset_split_params.test.set && this.dataset_split_params.test.group > 0) {
          params['test_dataset_id'] = this.dataset_split_params.test.d.id;
          params['test_set_id'] = this.dataset_split_params.test.set.id;
          params['test_group'] = this.dataset_split_params.test.group;
        }
      }
      if (this.groupSplit === 'version') {
        if (!this.dataset) {
          this.message = `Missing Dataset`;
          return;
        }

        if (!this.version_params.v) {
          this.message = `Missing Version`;
          return;
        }
        params['dataset_id'] = this.dataset.id;
        params['version'] = this.version_params.v;
      }
      if (this.groupSplit === 'default') {
        if (!this.default_split_params.train.d) {
          this.message = `Missing Train Dataset`;
          return;
        }
        params['train_dataset_id'] = this.default_split_params.train.d.id;

        if (!this.default_split_params.train.set) {
          this.message = `Missing Train Annotation Set`;
          return;
        }
        params['train_set_id'] = this.default_split_params.train.set.id;

        if (!this.default_split_params.val.d) {
          this.message = `Missing Val Dataset`;
          return;
        }
        params['val_dataset_id'] = this.default_split_params.val.d.id;

        if (!this.default_split_params.val.set) {
          this.message = `Missing Val Annotation Set`;
          return;
        }
        params['val_set_id'] = this.default_split_params.val.set.id;

        if (this.default_split_params.test.set) {
          params['test_dataset_id'] = this.default_split_params.test.d.id;
          params['test_set_id'] = this.default_split_params.test.set.id;
        }
      }

      if (this.name) {
        params.session_name = this.name;
      }
      if (this.description) {
        params.session_description = this.description;
      }
      if (this.schemaType) {
        params.trainer_type = this.schemaType;
      }

      this.startingTrainer = true;
      await this.dataConnect.startCloudInstance({
        params,
        type: 'trainer',
        name: params.session_name,
        project_id: this.currentProject.id,
        is_cloud: this.is_cloud,
        is_local: this.is_local,
        is_kubernetes: this.is_kubernetes,
      })
        .then((data) => {
          if (data.result) {
            event('create_training_session_success', {
              category: 'create_training_session',
              label: 'User successfully created a training session',
              user: this.$store.state.user.user.username,
            });
            this.getTasks();
            if (!this.isGettingTasks) {
              this.resumeGetTasks();
            }
            this.$refs.createTrainerModal.closeModal();
            this.$emit('create-trainer-session');
            $toast.open({
              queue: true,
              duration: 5000,
              message: `Train session in progress.`,
              type: 'success',
              position: 'top',
            });
          } else if (data.error) {
            event('create_training_session_failure', {
              category: 'create_training_session',
              label: 'User failed to create a training session',
              user: this.$store.state.user.user.username,
              reason: data.error.message,
            });
            $toast.open({
              queue: true,
              duration: 5000,
              message: data.error.message,
              type: 'error',
              position: 'top',
            });
          }
        })
        .catch((error) => {
          console.log(error);
        });
      this.startingTrainer = false;
    },
    getSessionName(session) {
      if (session.name) {
        return session.name;
      } else if (session.docker_task) {
        return session.docker_task.date;
      }
      return 'Unknown Session';
    },
    async getTrainingSessionList(trainer_id) {
      const resp = await this.dataConnect.getTrainingSessionList({ trainer_id, reverse: true })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve training sessions: ${error}`,
            type: "error",
            position: "top",
          });
        });
      if (!resp || resp.error || !resp.result) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to retrieve training sessions: ${resp.error.message}`,
          type: "error",
          position: "top",
        });
        return [];
      }

      return resp.result;
    },
  },
};

</script>

<style lang="scss" scoped>
// $cardMargin: 8px;
$cardPadding: 16px;

.settings {
  padding-top: 16px;
  padding-bottom: 16px;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  margin-left: auto;
  margin-right: auto;
  background: var(--color-white-600);

  span {
    width: 100%;
    font-size: 1rem;
    font-weight: 700;
    text-align: left;
    @include themify() {
      color: themed('color-primary');
    }
  }
}
.container {
  display: flex;
  flex-direction: row;
  padding: 0 16px;
  gap: 16px;
}

h2 {
    color: #250E81;
  }

.static-card {
  display: flex;
  flex-direction: column;
  position: relative;
  padding: $cardPadding;
  text-align: left;
  border-radius: 6px;
  height: fit-content;
  flex: 1 1 auto;
  border: 1px solid var(--color-primary-200);
  // margin: $cardMargin;
  width: 600px;
  background: var(--color-white-100);
  box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 0.05);
}

.dynamic-card {
  display: flex;
  flex-direction: column;
  position: relative;
  padding: $cardPadding;
  text-align: left;
  border-radius: 6px;
  height: fit-content;
  flex: 1 1 auto;
  border: 1px solid var(--color-primary-200);
  // margin: $cardMargin;
  width: 600px;
  background: var(--color-white-100);
  box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 0.05);
}

:deep(.dynamic-card > .container > .model-settings > .field-group:not(:first-of-type)) {
  padding-top: 24px;
}

@media (max-width: $breakpoint-lg) {
  .settings {
    width: 100%;
  }

  .container {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  // .static-card {
  //   width: calc(100% - (2 * #{$cardMargin}));
  // }

  // .dynamic-card {
  //   width: calc(100% - (2 * #{$cardMargin}));
  // }
}

.footer {
  display: flex;
  flex-direction: column;
  padding: 0.75rem 3rem;
  justify-content: flex-end;
  border-top: solid 3px var(--gray-300);
  background: var(--color-white-100);

  button {
    margin-left: auto;
  }
}

.result {
  display: flex;
  width: 100%;
  height: 100%;
  border-radius: 5px;
  font-size: 1rem;
  font-weight:600;
  padding: 10px;
  align-items: center;
  justify-content: center;

  span {
    width: 100%;
    line-break: anywhere;
  }
}

.error {
  @include themify() {
    background: rgba(182, 94, 94, 0.5) ;
    color: themed('color-error') !important;
  }
}

.radio-group {
  display: inline-block;
  margin: 20px;
  border-radius: 5px;
  overflow: hidden;
  @include themify() {
    border: solid 1px themed('color-primary');
  }
}

.radio-group:focus-within, .radio-group:active {
  @include themify() {
    box-shadow:0 0 5px themed('color-primary');
  }
}

.radio-group input[type="radio"] {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.radio-group input[type="radio"] + label {
  min-width: 6rem;
  max-width: 50%;
  text-align: center;
  display: inline-block;
  cursor: pointer;
  font-weight: bold;
  font-size: 1rem;
  line-height: 1.25;
  padding: 5px 20px;
  transition: all 0.4s ease;
  box-shadow: 0 0 3px 1px rgba(0, 0, 0, 0.25) inset;
  @include themify() {
    color: themed('color-primary');
    background: themed('color-white-100');
  }
}

.radio-group input[type="radio"]:not(:disabled):hover + label {
  @include themify() {
    background: themed('color-primary-200');
    color: themed('primary-text-color');
  }
}

.radio-group input[type="radio"]:checked + label,
.radio-group input[type="radio"]:checked:hover + label {
  opacity: 1;
  z-index: 1;
  box-shadow: 0 0 6px -2px rgba(0, 0, 0, 0.25), 0 0 6px 2px rgba(0, 0, 0, 0.25);
  @include themify() {
    background: themed('color-primary');
    color: themed('primary-text-color');
  }
}

.radio-group.full-width-selector {
  display: flex;
  width: 100%;
  margin: 0;
}

.radio-group.full-width-selector input[type="radio"] + label {
  min-width: 1px;
  padding: 3px 10px;
  flex: 1 1 auto;
}

.dataset-selector{
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: auto;

  :first-child {
    flex: 1 1 40%;
  }

  :not(:first-child) {
    flex: 1 1 60%;
  }
}

.trainer-session-row-switch {
  flex-direction: row !important;
  overflow: visible;
  align-items: center;

  label:first-child {
    flex: 1 1 40%;
    max-width: 40%;
  }
}

.full-width {
  width: 100%;
}

.custom-split-card {
  border-radius: 6px;
  padding: 10px;

  p {
    font-size: 0.9rem;
    font-weight: 700;
  }
}

.custom-split-card + .custom-split-card {
  margin-left: 15px;
}

.slider-settings {
  display: flex;
  flex-direction: row;
  padding: 12px;
  border-radius: inherit;
  gap: 6px;
  min-width: 350px;
  min-height: 50px;
  justify-content: center;
  align-items: center;
  overflow: auto;

  &__row {
    display: flex;
    align-items: center;
  }

  & input[type=checkbox] {
    margin-right: 12px;
  }

  & span {
    width: 40px;
    text-align: center;
  }
}

._train{
    border: 2px solid rgb(44, 145, 246);
}

._validation{
  @include themify {
    border: 2px solid themed('range-slider-selected-color');
  }
}

._test{
  @include themify {
    border: 2px solid themed('range-slider-selected-color-right');
  }
}

.clickable {
  cursor: pointer;
}

.split-radio[type="radio"] {
  height: 18px;
  width: 18px;
  @include themify() {
    accent-color: themed('color-primary');
  }
}

:deep() {
  .v-label{
    opacity: 1 !important;
  }
}

.checkbox-label {
  margin-left: 5px;
  font-size: 14px !important;
  font-weight: 600!important;
  letter-spacing: 0 !important;
  @include themify() {
    color: themed('form-label-color') !important;
  }
}

</style>
